/**
 * Prévia dos valores derivados no formulário. O back recalcula os dois ao
 * salvar (`allocation/domain/financials.ts`), com o mesmo arredondamento em
 * centavos — aqui é só para o usuário ver o resultado antes de salvar.
 */

/** Horas vendidas sugeridas quando o perfil tem preço (mesmo padrão do back e do legado). */
export const DEFAULT_SOLD_HOURS = 168;

const CENTS = 100;
const FULL_SHARE = 100;

export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * CENTS) / CENTS;
}

export function previewAllocatedCost(
  monthlyCost: number | null | undefined,
  allocatedPercentage: number | null | undefined,
): number | null {
  if (monthlyCost == null) return null;
  return roundMoney((monthlyCost * (allocatedPercentage ?? 0)) / FULL_SHARE);
}

export function previewGrossRevenue(
  hourlyRate: number | null | undefined,
  soldHours: number | null | undefined,
): number {
  return roundMoney((hourlyRate ?? 0) * (soldHours ?? 0));
}

export function previewNetRevenue(
  grossRevenue: number | null | undefined,
  taxRate: number | null | undefined,
): number {
  const bruto = grossRevenue ?? 0;
  if (!taxRate) return roundMoney(bruto);
  return roundMoney(bruto - (bruto * taxRate) / FULL_SHARE);
}

export function previewEbitda(
  netRevenue: number | null | undefined,
  allocatedCost: number | null | undefined,
): number | null {
  if (netRevenue == null) return null;
  return roundMoney(netRevenue - (allocatedCost ?? 0));
}

export function previewEbitdaPercentage(
  netRevenue: number | null | undefined,
  allocatedCost: number | null | undefined,
): number | null {
  const ebitda = previewEbitda(netRevenue, allocatedCost);
  if (ebitda === null) return null;
  if (!netRevenue) return 0;
  return roundMoney((ebitda / netRevenue) * FULL_SHARE);
}

/** Valor com a moeda dele (o Axiis nunca converte: BRL fica BRL, USD fica USD). */
export function formatMoney(
  value: number | null | undefined,
  currencyIso: string | undefined,
  locale: string,
): string {
  if (value == null) return '';
  return new Intl.NumberFormat(locale, {
    ...(currencyIso ? { style: 'currency', currency: currencyIso } : {}),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * `format`/`parse` do `NumberInput` no idioma da tela (pt-BR mostra
 * `1.234,50` fora de foco). Em foco o componente mostra `toFixed` (sempre
 * com ponto: `1234.50`), então o `parse` aceita os dois: com o separador
 * decimal do idioma, tira os de milhar; sem ele, lê o ponto como decimal.
 */
export function decimalInputFormat(locale: string) {
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const parts = formatter.formatToParts(1234.5);
  const group = parts.find((part) => part.type === 'group')?.value ?? ',';
  const decimal = parts.find((part) => part.type === 'decimal')?.value ?? '.';
  return {
    format: (value: number) => formatter.format(value),
    parse: (text: string) => {
      const compact = text.replace(/\s/g, '');
      if (decimal !== '.' && !compact.includes(decimal)) return Number(compact);
      return Number(compact.split(group).join('').replace(decimal, '.'));
    },
  };
}
